function Card({ title, children, accent = 'cyan' }) {
  const accentColors = {
    cyan: 'border-cyan-500/30 bg-cyan-950/20',
    emerald: 'border-emerald-500/30 bg-emerald-950/20',
    amber: 'border-amber-500/30 bg-amber-950/20',
    rose: 'border-rose-500/30 bg-rose-950/20',
  }
  return (
    <div className={`p-4 rounded-xl border ${accentColors[accent]} text-slate-200`}>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{title}</h4>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  )
}

function formatScore(v) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—'
  return Number(v).toFixed(1)
}

function formatPct(v) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—'
  return `${(Number(v) * 100).toFixed(2)}%`
}

export default function MonteCarloInsights({ insights }) {
  if (!insights) return null

  const {
    strongest_asset,
    weakest_asset,
    strongest_timeframe,
    weakest_timeframe,
    strongest_combination,
    weakest_combination,
    indicator_notes,
    recommendation,
  } = insights

  return (
    <div className="p-4 space-y-4 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200">
      <h3 className="text-sm font-semibold text-slate-100">Insights y recomendación</h3>

      {recommendation && (
        <div className="p-3 rounded-lg bg-slate-800 border border-slate-600 text-sm leading-relaxed">
          {recommendation}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title="Activo más robusto" accent="emerald">
          {strongest_asset ? (
            <div className="space-y-1">
              <p className="font-bold text-emerald-400">{strongest_asset.asset}</p>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Score" value={formatScore(strongest_asset.avg_score)} />
                <Metric label="Trades" value={strongest_asset.total_trades ?? '—'} />
                <Metric label="Net profit" value={formatPct(strongest_asset.avg_net_profit_pct)} />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No hay datos suficientes.</p>
          )}
        </Card>

        <Card title="Activo más débil" accent="rose">
          {weakest_asset ? (
            <div className="space-y-1">
              <p className="font-bold text-rose-400">{weakest_asset.asset}</p>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Score" value={formatScore(weakest_asset.avg_score)} />
                <Metric label="Trades" value={weakest_asset.total_trades ?? '—'} />
                <Metric label="Net profit" value={formatPct(weakest_asset.avg_net_profit_pct)} />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No hay datos suficientes.</p>
          )}
        </Card>

        <Card title="Timeframe más robusto" accent="emerald">
          {strongest_timeframe ? (
            <div className="space-y-1">
              <p className="font-bold text-emerald-400">{strongest_timeframe.timeframe}</p>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Score" value={formatScore(strongest_timeframe.avg_score)} />
                <Metric label="Trades" value={strongest_timeframe.total_trades ?? '—'} />
                <Metric label="Net profit" value={formatPct(strongest_timeframe.avg_net_profit_pct)} />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No hay datos suficientes.</p>
          )}
        </Card>

        <Card title="Timeframe más débil" accent="rose">
          {weakest_timeframe ? (
            <div className="space-y-1">
              <p className="font-bold text-rose-400">{weakest_timeframe.timeframe}</p>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Score" value={formatScore(weakest_timeframe.avg_score)} />
                <Metric label="Trades" value={weakest_timeframe.total_trades ?? '—'} />
                <Metric label="Net profit" value={formatPct(weakest_timeframe.avg_net_profit_pct)} />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No hay datos suficientes.</p>
          )}
        </Card>

        <Card title="Mejor combinación" accent="cyan">
          {strongest_combination ? (
            <div className="space-y-1">
              <p className="font-bold text-cyan-400">
                {strongest_combination.asset} · {strongest_combination.timeframe}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Score" value={formatScore(strongest_combination.score)} />
                <Metric label="Trades" value={strongest_combination.total_trades ?? '—'} />
                <Metric label="Net profit" value={formatPct(strongest_combination.net_profit_pct)} />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No hay datos suficientes.</p>
          )}
        </Card>

        <Card title="Combinación más débil" accent="amber">
          {weakest_combination ? (
            <div className="space-y-1">
              <p className="font-bold text-amber-400">
                {weakest_combination.asset} · {weakest_combination.timeframe}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Score" value={formatScore(weakest_combination.score)} />
                <Metric label="Trades" value={weakest_combination.total_trades ?? '—'} />
                <Metric label="Net profit" value={formatPct(weakest_combination.net_profit_pct)} />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No hay datos suficientes.</p>
          )}
        </Card>
      </div>

      {indicator_notes?.length > 0 && (
        <div className="text-xs text-slate-400 space-y-1">
          <p className="font-semibold text-slate-300">Notas por indicador:</p>
          <ul className="list-disc list-inside">
            {indicator_notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-[10px] text-slate-500">
        Disclaimer: estos insights son un análisis estadístico automatizado, no asesoramiento financiero.
      </p>
    </div>
  )
}
